<template>
  <div class="probe-result">
    <div v-if="!result" class="probe-empty">Нет данных проверки</div>

    <template v-else>
      <div v-if="result.token_present === false" class="probe-warn">
        Токен не задан — проверка пропущена.
      </div>

      <!-- Шаг 1: /me -->
      <div class="probe-block">
        <div class="probe-head">
          <span class="probe-step">Шаг 1 · Данные ATI (/me)</span>
          <span class="probe-status" :class="meStatusClass">{{ meStatusText }}</span>
        </div>
        <div v-if="me" class="probe-me">
          <div v-if="me.ok && me.account">
            <div v-if="me.account.FullFirmName" class="me-line firm">{{ me.account.FullFirmName }}</div>
            <div class="me-grid">
              <span v-if="me.account.Inn"><b>ИНН:</b> {{ me.account.Inn }}</span>
              <span v-if="me.account.FirmType"><b>Тип:</b> {{ me.account.Ownership }} {{ me.account.FirmType }}</span>
              <span v-if="loc"><b>Регион:</b> {{ loc }}</span>
              <span v-if="me.account.LoadsAccess !== undefined"><b>LoadsAccess:</b> {{ me.account.LoadsAccess }}</span>
            </div>
            <div v-if="logist" class="me-logist">
              <span v-if="logist.Name"><b>Логист:</b> {{ logist.Name }}</span>
              <span v-if="logist.Mobile"><b>Тел:</b> {{ logist.Mobile }}</span>
              <span v-if="logist.Email"><b>Почта:</b> {{ logist.Email }}</span>
            </div>
            <div v-if="contactMismatch" class="me-warn">
              ⚠ ContactId в ATI ({{ me.account.ContactId }}) не совпадает с ID контакта аккаунта ({{ expectedContactId }}).
            </div>
          </div>
          <div v-else class="me-error">
            {{ me.error === 'unauthorized' ? 'Токен невалиден (401)' : (me.error || `HTTP ${me.status}`) }}
          </div>
        </div>
      </div>

      <!-- Шаг 2: publish-test -->
      <div class="probe-block">
        <div class="probe-head">
          <span class="probe-step">Шаг 2 · Тест публикации</span>
          <span class="probe-status" :class="publishClass">{{ publishText }}</span>
        </div>
        <div v-if="publish" class="probe-publish">
          <span v-if="publish.cargo_number" class="pub-line">Создан {{ publish.cargo_number }}</span>
          <span v-if="publish.deleted === true" class="pub-line pub-ok">тест-груз удалён ✓</span>
          <span v-else-if="publish.deleted === false" class="pub-line pub-warn">тест-груз НЕ удалён — проверьте ATI вручную (id: {{ publish.created_id }})</span>
          <span v-if="publish.status" class="pub-line">HTTP {{ publish.status }}</span>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import { computed } from 'vue'

const PUBLISH_META = {
  publish_ok: { text: 'Публикует и удаляет', cls: 'ok' },
  duplicate_ok: { text: 'Валиден, разрешён (дубль)', cls: 'ok' },
  validated: { text: 'Токен валиден (payload отвергнут)', cls: 'warn' },
  unauthorized: { text: 'Невалиден / истёк (401)', cls: 'bad' },
  business_block: { text: 'Бизнес-блок (403)', cls: 'bad' },
  no_connection: { text: 'Нет связи с ATI', cls: 'warn' },
  account_lookup_failed: { text: 'Служба аккаунтов недоступна', cls: 'warn' },
  skipped: { text: 'Пропущено', cls: 'muted' },
  skipped_no_token: { text: 'Токен не задан', cls: 'muted' },
}

export default {
  name: 'TokenProbeResult',
  props: {
    result: { type: Object, default: null },
    expectedContactId: { type: [Number, String], default: null },
  },
  setup(props) {
    const me = computed(() => props.result?.me || null)
    const publish = computed(() => props.result?.publish || null)

    const loc = computed(() => {
      const l = me.value?.account?.Location
      if (!l) return ''
      return [l.CityName, l.RegionName].filter(Boolean).join(', ')
    })
    const logist = computed(() => {
      const acc = me.value?.account
      if (!acc) return null
      const contacts = acc.Contacts || []
      const main = contacts.find((c) => c.Id === acc.ContactId) || contacts[0] || null
      return main
    })

    const contactMismatch = computed(() => {
      if (!me.value?.ok || !me.value?.account) return false
      if (props.expectedContactId == null) return false
      return String(me.value.account.ContactId) !== String(props.expectedContactId)
    })

    const meStatusClass = computed(() => (me.value?.ok ? 'ok' : 'bad'))
    const meStatusText = computed(() => (me.value?.ok ? 'OK' : `Ошибка${me.value?.status ? ' ' + me.value.status : ''}`))

    const publishMeta = computed(() => {
      const v = publish.value?.verdict
      return v && PUBLISH_META[v] ? PUBLISH_META[v] : { text: v || '—', cls: 'muted' }
    })
    const publishClass = computed(() => publishMeta.value.cls)
    const publishText = computed(() => publishMeta.value.text)

    return {
      me, publish, loc, logist, contactMismatch,
      meStatusClass, meStatusText, publishClass, publishText,
    }
  },
}
</script>

<style scoped>
.probe-result { display: flex; flex-direction: column; gap: 10px; }
.probe-empty, .probe-warn { background: #f1f5f9; border: 1px solid #e2e8f0; border-radius: 8px; color: #475569; font-size: 12px; padding: 8px 10px; }
.probe-block { border: 1px solid #e5e7eb; border-radius: 8px; padding: 8px 10px; }
.probe-head { display: flex; justify-content: space-between; align-items: center; margin-bottom: 6px; }
.probe-step { font-size: 12px; font-weight: 700; color: #334155; }
.probe-status { font-size: 11px; font-weight: 700; padding: 1px 8px; border-radius: 10px; }
.probe-status.ok { background: #e6f4ea; color: #1e7e34; }
.probe-status.bad { background: #fee2e2; color: #991b1b; }
.probe-status.warn { background: #fef3c7; color: #92400e; }
.probe-status.muted { background: #f1f5f9; color: #6b7280; }
.probe-me { font-size: 12px; color: #1f2937; line-height: 1.5; }
.me-line.firm { font-weight: 700; margin-bottom: 4px; }
.me-grid { display: flex; flex-wrap: wrap; gap: 6px 14px; color: #475569; }
.me-grid b { color: #1f2937; }
.me-passport { color: #475569; margin-top: 4px; }
.me-logist { display: flex; flex-wrap: wrap; gap: 4px 14px; margin-top: 6px; padding: 6px 8px; background: #f1f5f9; border-radius: 6px; color: #1f2937; }
.me-logist b { color: #475569; font-weight: 700; }
.me-warn { color: #92400e; background: #fef3c7; border-radius: 6px; padding: 4px 8px; margin-top: 6px; font-size: 11px; }
.me-error { color: #991b1b; font-size: 12px; }
.probe-publish { display: flex; flex-direction: column; gap: 2px; font-size: 12px; color: #475569; }
.pub-line b { color: #1f2937; }
.pub-ok { color: #1e7e34; }
.pub-warn { color: #92400e; }
</style>
